import React, { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { User, UserRole } from '../../types/user';
import { useAdmin } from '../../contexts/adminContext';

interface Props {
  open: boolean;
  user: User | null;
  onClose: () => void;
  onChangePassword?: (userId: string, newPassword: string) => Promise<void>;
  darkMode?: boolean;
  backendUrl: string;
  onUserUpdated?: () => void;
}

const roles: UserRole[] = ['Admin', 'User'];

const EditUserModal: React.FC<Props> = ({
  open,
  user,
  onClose,
  onChangePassword,
  darkMode = false,
  backendUrl,
  onUserUpdated
}) => {
  const [form, setForm] = useState<Partial<User>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const {
    fetchRecentMeetings,
    fetchTopUsers,
    fetchAllUserData,
    updateUserPassword
  } = useAdmin();

  useEffect(() => {
    if (user && open) {
      setForm({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        department: user.department,
        position: user.position,
        location: user.location,
        role: user.role,
        avatarUrl: user.avatarUrl,
      });
      setErrors({});
      setApiError(null);
      setPassword('');
      setPassword2('');
      setPwErr(null);
      setShowPassword(false);
    }
  }, [user, open]);

  if (!open || !user) return null;

  const validate = () => {
    const err: Record<string, string> = {};
    if (!form.firstName?.trim()) err.firstName = 'First name is required';
    if (!form.lastName?.trim()) err.lastName = 'Last name is required';
    if (!form.email?.trim()) err.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = 'Invalid email';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const editUser = async (userId: string, updates: Partial<User>) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const backendData = {
      first_name: updates.firstName,
      last_name: updates.lastName,
      email: updates.email,
      department: updates.department,
      job_title: updates.position,
      isAdmin: updates.role === 'Admin',
      phone_number: updates.phoneNumber,
      location: updates.location,
    };

    const response = await fetch(`${backendUrl}/api/admin/edit-users/${userId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(backendData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to edit user');
    }

    return await response.json();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (showPassword) {
      if (password.length < 8) {
        setPwErr('Password must be at least 8 characters');
        return;
      }
      if (password !== password2) {
        setPwErr('Passwords do not match');
        return;
      }
    }

    setSubmitting(true);
    setApiError(null);

    try {
      await editUser(user.id, form);
      fetchRecentMeetings();
      fetchTopUsers();
      fetchAllUserData();

      if (showPassword && password) {
        await updateUserPassword(user.id, password);
      }

      if (onUserUpdated) onUserUpdated();
      onClose();
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'Failed to edit user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`${darkMode ? 'bg-[#232336] text-white border border-white/10' : 'bg-white text-black'} w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-6 shadow-xl`}>
        
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit User</h3>
          <button onClick={onClose} className={`${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
            <X />
          </button>
        </div>

        {apiError && (
          <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300 text-red-700 text-sm">
            {apiError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">First Name</label>
              <input 
                value={form.firstName || ''} 
                onChange={e => setForm({ ...form, firstName: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`} 
              />
            </div>

            <div>
              <label className="block text-sm mb-1">Last Name</label>
              <input 
                value={form.lastName || ''} 
                onChange={e => setForm({ ...form, lastName: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`} 
              />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Email</label>
            <input 
              value={form.email || ''} 
              readOnly
              className={`w-full border rounded-lg px-3 py-2 cursor-not-allowed ${darkMode ? 'bg-white/10 border-white/10 text-white/60' : 'bg-gray-100 text-gray-500'}`} 
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">Phone Number</label>
              <input 
                value={form.phoneNumber || ''} 
                onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`} 
              />
            </div>

            <div>
              <label className="block text-sm mb-1">Department</label>
              <input 
                value={form.department || ''} 
                onChange={e => setForm({ ...form, department: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`} 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1">Position</label>
              <input 
                value={form.position || ''} 
                onChange={e => setForm({ ...form, position: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`} 
              />
            </div>

            <div>
              <label className="block text-sm mb-1">Location</label>
              <input 
                value={form.location || ''} 
                onChange={e => setForm({ ...form, location: e.target.value })}
                className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`} 
              />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Role</label>
            <select
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value as UserRole })}
              className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`}
            >
              {roles.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" className="sr-only" checked={showPassword} onChange={() => setShowPassword(!showPassword)} />
            <span className={`h-5 w-5 rounded border flex items-center justify-center ${showPassword ? 'bg-gradient-to-r from-red-600 to-pink-600 border-transparent' : darkMode ? 'border-white/20' : 'border-gray-300'}`}>
              <Check size={12} className={`text-white ${showPassword ? 'opacity-100' : 'opacity-0'}`} />
            </span>
            <span className="text-sm">Update password</span>
          </label>

          {showPassword && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1">New Password</label>
                <input 
                  type="password" 
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`} 
                />
              </div>

              <div>
                <label className="block text-sm mb-1">Confirm Password</label>
                <input 
                  type="password" 
                  value={password2}
                  onChange={e => setPassword2(e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${darkMode ? 'bg-[#0f1420] border-white/10 text-white' : 'border-gray-300'}`} 
                />
              </div>

              {pwErr && <div className="text-xs text-red-500 sm:col-span-2">{pwErr}</div>}
            </div>
          )}

          <div className="flex justify-between gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-white/30 text-white' : 'border-gray-300'}`}
            >
              Cancel
            </button>

            <button 
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 text-white disabled:opacity-60"
            >
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
